import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { useLocalStorage } from '@ui/hooks/useLocalStorage';

import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import {
  useSession,
  useProperties,
  saveSessionData,
  cacheProperty,
  useFavouriteProperties
} from 'services/guestAiPromptSessions';
import { usePropertyFilters } from './usePropertyFilters';
import { useFlagsWithInit } from './useFlags';

const GuestPropertyFinderSessionContext = React.createContext(null);

export function GuestPropertyFinderSessionContextProvider({ children }) {
  const params = useParams();
  const location = window.location;

  const { isUserDefinedFiltersEnabled } = useFlagsWithInit();
  const [sessionUuid] = useState(params.sessionId || uuidv4());
  const [messages, setMessages] = useState([]);
  const [propertiesFeedback, setPropertiesFeedback] = useState({});
  const [isPropertiesLoading, setIsPropertiesLoading] = useState(false);

  // Default to 'liked' to avoid unnecessary Casafari calls
  // Switch to 'all' only when user explicitly navigates from passport recommendations
  const initialListViewType = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('view') === 'all' ? 'all' : 'liked';
  }, [location.search]);

  const [listViewType, setListViewType] = useState(initialListViewType);
  const [guestUuid] = useLocalStorage('property-finder-guest-uuid', uuidv4());
  const [activeFilters, setActiveFilters] = useState(null);
  // Track if current filters are from manual user selection (vs AI-initiated search)
  const [isUserManualFilters, setIsUserManualFilters] = useState(false);

  const {
    data: session = {},
    isLoading: isSessionLoading,
    refetch: reloadSession,
    error: sessionError
  } = useSession({
    enabled: !!sessionUuid,
    sessionId: sessionUuid
  });

  // Enable properties loading if:
  // 1. AI chat session (has loaded_json_uuid_reference), OR
  // 2. Passport-generated session (has location_data or properties_data)
  const hasPropertySearchData = useMemo(() => {
    const hasData = !!(
      session?.loaded_json_uuid_reference ||
      session?.parsed_json?.location_data ||
      session?.parsed_json?.properties_data
    );

    return hasData;
  }, [session]);

  const propertiesState = useProperties({
    enabled: hasPropertySearchData && listViewType === 'all',
    sessionId: sessionUuid,
    userFilters: activeFilters
  });

  const favouritePropertiesState = useFavouriteProperties({
    enabled: hasPropertySearchData && listViewType === 'liked',
    sessionId: sessionUuid
  });

  // Handle filter changes - receives filter messages or regular filter data
  // NOTE: We do NOT manually call refetch() here. React Query automatically refetches
  // when the queryKey changes (which includes userFilters/activeFilters).
  const handleFiltersChange = useCallback(
    (filterData) => {
      if (filterData.isFilterUpdate && filterData.filterMessage) {
        // Handle filter update message (user role, for AI to respond to)
        if (window.propertyFinderPostMessage) {
          // Send filter message to AI chat - AI will acknowledge the changes
          window.propertyFinderPostMessage(filterData.filterMessage.content, {
            type: 'filter_update',
            isFilterUpdate: true,
            filters: filterData.filters
          });
        }

        // Update messages state to include the filter message
        // Only store role and content for OpenAI compatibility
        const cleanMessage = {
          role: filterData.filterMessage.role,
          content: filterData.filterMessage.content
        };
        setMessages((prev) => [...prev, cleanMessage]);

        // Store filters for UI state - React Query will automatically refetch
        // because activeFilters is part of the useProperties queryKey
        setActiveFilters(filterData.filters || filterData);
        // Mark these as user-applied filters (not AI-initiated)
        setIsUserManualFilters(true);

        // Reload session to get updated search summary (with delay to avoid race conditions)
        setTimeout(() => {
          reloadSession();
        }, 1000); // Small delay to ensure backend processing is complete
      } else if (filterData.filterMessage) {
        // Legacy support for regular filter messages
        if (window.propertyFinderPostMessage) {
          window.propertyFinderPostMessage(filterData.filterMessage);
        }

        // Store filters for UI state
        setActiveFilters(filterData.filters || filterData);
        setIsUserManualFilters(true);
      } else {
        // Store filters for UI state
        setActiveFilters(filterData.filters || filterData);
        setIsUserManualFilters(true);
      }
    },
    [reloadSession]
  );

  // Initialize property filters hook
  const propertyFilters = usePropertyFilters({
    sessionId: sessionUuid,
    initialFilters: null, // Let it load from database only
    onFiltersChange: handleFiltersChange,
    messages: messages,
    session: session
  });

  const reloadPropertiesAndSession = useCallback(
    async (options = {}) => {
      const { forceUseAIFilters = false } = options;

      setIsPropertiesLoading(true);

      // If user manually applied filters (via Search button), keep them
      // Only clear filters if AI initiated a new search OR explicitly requested
      if (isUserManualFilters && !forceUseAIFilters) {
        // Just refetch with current user filters (already in activeFilters)
      } else {
        // Clear user filters so AI's new search criteria are used
        setActiveFilters(null);
        setIsUserManualFilters(false);
      }

      // Reload session first to get latest data
      await reloadSession();

      // Explicitly refetch properties after a microtask to ensure state has updated
      await new Promise((resolve) => setTimeout(resolve, 100));
      await propertiesState.refetch();

      // Reload session AGAIN after properties fetch to get updated loaded_json_uuid_reference
      // This is needed so hasNewPropertiesQuery becomes false (toast disappears)
      await reloadSession();

      setIsPropertiesLoading(false);

      // Reset isUserManualFilters after "View Properties" is clicked
      // This ensures subsequent AI searches will use AI's criteria
      if (isUserManualFilters && !forceUseAIFilters) {
        setIsUserManualFilters(false);
      }

      // Only refresh filters UI from AI if we're using AI's criteria
      if (!isUserManualFilters || forceUseAIFilters) {
        if (propertyFilters?.refreshFiltersFromAI) {
          propertyFilters.refreshFiltersFromAI();
          setTimeout(() => {
            propertyFilters.refreshFiltersFromAI();
          }, 1000);
        }
      }
    },
    [reloadSession, propertyFilters, propertiesState, isUserManualFilters]
  );

  const togglePropertiesFeedback = useCallback(
    async (type, propertyId, property) => {
      const types = ['liked_properties', 'disliked_properties'];
      const unselectedType = types.find((t) => t !== type);

      const initialFeedbackState = {
        ...propertiesFeedback
      };

      if (propertiesFeedback[type][propertyId]) {
        delete propertiesFeedback[type][propertyId];
      } else {
        propertiesFeedback[type][propertyId] = true;
        delete propertiesFeedback[unselectedType][propertyId];
      }

      setPropertiesFeedback({
        ...propertiesFeedback
      });

      try {
        const cachePropertyResponse = await cacheProperty(sessionUuid, propertyId, {
          type,
          property
        });

        if (!cachePropertyResponse?.data?.error) {
          await saveSessionData(sessionUuid, { properties_feedback: propertiesFeedback }).catch(
            () => {
              setPropertiesFeedback(initialFeedbackState);
            }
          );
        } else {
          await reloadPropertiesAndSession();
        }
      } catch (e) {
        console.error(e);
      }
    },
    [setPropertiesFeedback, sessionUuid, propertiesFeedback, reloadPropertiesAndSession]
  );

  const hasMessages = useMemo(() => !!messages?.length, [messages]);

  // Show "new properties" indicator when AI generates properties that user hasn't viewed yet
  const hasNewPropertiesQuery = useMemo(
    () =>
      session?.last_json_uuid_reference &&
      session?.loaded_json_uuid_reference !== session?.last_json_uuid_reference,
    [session]
  );

  useEffect(() => {
    setMessages(session?.messages || []);
  }, [session?.messages]);

  useEffect(() => {
    setPropertiesFeedback(session?.properties_feedback || {});
  }, [session?.properties_feedback]);

  // When session loads with database filters, populate the filter UI
  // ONLY on initial load, NOT on every AI response
  useEffect(() => {
    if (
      session?.parsed_json?.properties_data?.length > 0 &&
      propertyFilters?.refreshFiltersFromAI
    ) {
      // Small delay to ensure all session data is loaded
      setTimeout(() => {
        propertyFilters.refreshFiltersFromAI();
      }, 100);
    } else {
      console.log('🤖 AI response detected - NOT overwriting user filters');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.parsed_json?.properties_data]); // Intentionally excluding unstable refs to prevent infinite loops

  // REMOVED: Auto-refresh useEffects that were interfering with user input
  // refreshFiltersFromAI() is now only called explicitly when needed

  const properties = useMemo(() => {
    if (listViewType === 'liked') {
      return favouritePropertiesState?.data?.properties || [];
    }

    // If user has applied filters, prioritize API-loaded properties (filtered results)
    if (
      activeFilters &&
      Object.keys(activeFilters).some((key) => {
        const value = activeFilters[key];
        return value && (Array.isArray(value) ? value.length > 0 : true);
      })
    ) {
      console.log('🔍 Using filtered API properties due to active filters');
      return propertiesState?.data?.pages?.map((page) => page?.properties || [])?.flat() || [];
    }

    // Check if we have AI-generated properties in session (when no filters applied)
    if (session?.parsed_json?.properties_data?.length > 0) {
      console.log('🤖 Using AI-generated properties from session');
      return session.parsed_json.properties_data;
    }

    // Fall back to API-loaded properties
    console.log('📡 Using API-loaded properties as fallback');
    return propertiesState?.data?.pages?.map((page) => page?.properties || [])?.flat() || [];
  }, [
    listViewType,
    favouritePropertiesState,
    propertiesState,
    session?.parsed_json?.properties_data,
    activeFilters
  ]);

  const hasRateLimitError = useMemo(() => {
    return !!propertiesState.data?.pages?.[0]?.rate_limit;
  }, [propertiesState]);

  const propertiesError = useMemo(() => {
    if (listViewType === 'liked') {
      return favouritePropertiesState?.error || null;
    }

    return propertiesState?.error || null;
  }, [listViewType, favouritePropertiesState, propertiesState]);

  const favouritesPropertiesCount = useMemo(() => {
    return Object.keys(propertiesFeedback?.liked_properties || {}).length;
  }, [propertiesFeedback]);

  const propertiesCount = useMemo(() => {
    // Always return the TOTAL count of ALL properties matching the current filters

    if (
      activeFilters &&
      Object.keys(activeFilters).some((key) => {
        const value = activeFilters[key];
        return value && (Array.isArray(value) ? value.length > 0 : true);
      })
    ) {
      const firstPage = propertiesState?.data?.pages?.[0];
      if (firstPage?.count !== undefined) {
        return firstPage.count;
      }
    }

    if (session?.parsed_json?.properties_data?.length > 0) {
      return session.parsed_json.properties_data.length;
    }

    const firstPage = propertiesState?.data?.pages?.[0];
    if (firstPage?.count !== undefined) {
      return firstPage.count;
    }

    if (propertiesState?.data?.pages?.length > 0) {
      const allProperties = propertiesState.data.pages.map((page) => page?.properties || []).flat();
      return allProperties.length;
    }

    // 5. If nothing is available, return 0
    console.log('No properties count available, returning 0');
    return 0;
  }, [propertiesState, session?.parsed_json?.properties_data, activeFilters]);

  return (
    <GuestPropertyFinderSessionContext.Provider
      value={{
        session,
        isSessionLoading,
        reloadSession,
        sessionError,

        propertiesError,
        properties,
        isPropertiesLoading:
          isPropertiesLoading || propertiesState?.isLoading || propertiesState?.isFetching,
        reloadProperties: reloadPropertiesAndSession,
        hasNewPropertiesQuery,
        propertiesFeedback,
        togglePropertiesFeedback,

        favouriteProperties: favouritePropertiesState?.data?.properties || [],
        isFavouritePropertiesLoading: favouritePropertiesState?.isLoading,
        reloadFavouriteProperties: favouritePropertiesState?.refetch,

        messages,
        setMessages,
        hasMessages,

        sessionUuid,

        listViewType,
        setListViewType,

        favouritesPropertiesCount,
        propertiesCount,

        hasNextPage: listViewType !== 'liked' && propertiesState?.hasNextPage,
        fetchNextPage: propertiesState?.fetchNextPage,

        guestUuid,

        hasRateLimitError,

        // Property filters
        isUserDefinedFiltersEnabled,
        propertyFilters,
        activeFilters,
        handleFiltersChange,

        // Reset user manual filters flag when user sends a new AI message
        // This ensures AI-initiated searches use AI's criteria
        resetUserManualFilters: () => {
          setIsUserManualFilters(false);
        }
      }}>
      {children}
    </GuestPropertyFinderSessionContext.Provider>
  );
}

export function useGuestPropertyFinderSession() {
  const context = useContext(GuestPropertyFinderSessionContext);

  if (!context) {
    throw new Error(
      'useGuestPropertyFinderSession must be used within a GuestPropertyFinderSessionContextProvider'
    );
  }

  return context;
}
